/**
 * InstallPrompt - Smart banner to encourage PWA installation
 * Shows after 3 visits, can be dismissed for 7 days
 * Handles both Chrome (beforeinstallprompt) and Safari (manual instructions)
 */

import { X, Download, Smartphone, Share, PlusSquare, MoreHorizontal, ArrowDown, ExternalLink } from 'lucide-react';
import { useInstallPrompt, safariMajor } from '../../../hooks/useInstallPrompt';
import { useTenant } from '../../../contexts/TenantContext';

export default function InstallPrompt() {
  const { canShow, isSafari, isIOSSafari, inApp, install, dismiss, markAlreadyInstalled } = useInstallPrompt();
  // Safari 26 (iOS 26) range « Partager » derrière « ••• », en bas à droite ;
  // avant, c'était l'icône carrée au centre de la barre du bas. Sur iPad, le
  // bouton est en haut à droite : pas de flèche vers le bas.
  const ipad = /iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const safari26 = (safariMajor() ?? 0) >= 26;
  const etape = 'w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold flex-shrink-0';
  const { tenant } = useTenant();
  const primaryColor = tenant?.config?.ecommerce?.primaryColor || '#10b981';
  const storeName = tenant?.config?.ecommerce?.storeName || 'Folie Boutique';

  if (!canShow) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 z-40 md:bottom-4 md:left-auto md:right-4 md:w-96 animate-slide-up">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 relative overflow-hidden">
        {/* Accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ backgroundColor: primaryColor }}
        />

        <button
          onClick={dismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-8">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <Smartphone className="w-6 h-6" style={{ color: primaryColor }} />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">
              Installez {storeName}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Accédez à votre boutique bio directement depuis votre écran d'accueil
            </p>

            {inApp ? (
              /* Navigateur d'une application (Facebook, Instagram…) */
              <div className="mt-3 space-y-2 text-xs text-gray-700">
                <p>
                  Vous êtes dans le navigateur de l'application d'où vient le lien : l'installation n'y
                  est pas possible.
                </p>
                <p className="flex items-center gap-1">
                  <ExternalLink className="w-4 h-4 inline flex-shrink-0" style={{ color: primaryColor }} />
                  <span>
                    Touchez <span className="font-semibold">•••</span> (en haut ou en bas) puis{' '}
                    <span className="font-semibold">« Ouvrir dans {/iPhone|iPad|iPod/.test(navigator.userAgent) ? 'Safari' : 'le navigateur'} »</span>
                    , et installez la boutique depuis là.
                  </span>
                </p>
                <div className="mt-1">
                  <button onClick={dismiss} className="px-3 py-1.5 text-xs text-gray-600 font-medium">
                    J'ai compris
                  </button>
                </div>
              </div>
            ) : isSafari ? (
              /* Safari — manual instructions */
              <div className="mt-3 space-y-2">
                {isIOSSafari && safari26 && !ipad ? (
                  <>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className={etape}>1</span>
                      <span className="flex items-center gap-1 flex-wrap">
                        Touchez <MoreHorizontal className="w-5 h-5 inline text-blue-500" aria-label="•••" /> en bas à droite de l'écran
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className={etape}>2</span>
                      <span className="flex items-center gap-1">
                        <Share className="w-4 h-4 inline text-blue-500" />
                        <span className="font-semibold">Partager</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className={etape}>3</span>
                      <span className="flex items-center gap-1 flex-wrap">
                        <PlusSquare className="w-4 h-4 inline text-gray-600" />
                        <span className="font-semibold">Sur l'écran d'accueil</span>
                        <span className="text-gray-500">(parfois sous « Afficher plus »)</span>
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className={etape}>1</span>
                      <span className="flex items-center gap-1 flex-wrap">
                        Appuyez sur
                        {isIOSSafari ? (
                          <>
                            <Share className="w-4 h-4 inline text-blue-500" />
                            <span className="font-semibold">Partager</span>
                            <span className="text-gray-500">
                              {ipad ? '(en haut à droite)' : '(en bas, au centre)'}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">Fichier</span>
                            <span>dans la barre de menu</span>
                          </>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-700">
                      <span className={etape}>2</span>
                      <span className="flex items-center gap-1">
                        {isIOSSafari ? (
                          <>
                            <PlusSquare className="w-4 h-4 inline text-gray-600" />
                            <span className="font-semibold">Sur l'écran d'accueil</span>
                          </>
                        ) : (
                          <span className="font-semibold">Ajouter au Dock</span>
                        )}
                      </span>
                    </div>
                  </>
                )}
                <div className="mt-1 flex items-center gap-3">
                  <button
                    onClick={dismiss}
                    className="px-3 py-1.5 text-xs text-gray-600 font-medium"
                  >
                    J'ai compris
                  </button>
                  <button
                    onClick={markAlreadyInstalled}
                    className="px-3 py-1.5 text-xs font-semibold underline"
                    style={{ color: primaryColor }}
                  >
                    J'ai déjà l'app
                  </button>
                </div>
              </div>
            ) : (
              /* Chrome / Edge / Firefox — standard install prompt */
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={async () => {
                    await install();
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold active:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Download className="w-4 h-4" />
                  Installer
                </button>
                <button
                  onClick={dismiss}
                  className="px-3 py-2 text-sm text-gray-600 font-medium"
                >
                  Plus tard
                </button>
                <button
                  onClick={markAlreadyInstalled}
                  className="px-3 py-2 text-sm font-medium underline"
                  style={{ color: primaryColor }}
                >
                  Déjà installée
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Flèche vers le bouton de Safari, sous la page : au centre avant
          iOS 26 (icône Partager), à droite depuis (« ••• »). */}
      {isIOSSafari && !ipad && !inApp && (
        <div className={`flex mt-1 ${safari26 ? 'justify-end pr-4' : 'justify-center'}`} aria-hidden="true">
          <ArrowDown className="w-7 h-7 animate-bounce" style={{ color: primaryColor }} />
        </div>
      )}
    </div>
  );
}
